import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface Profile {
  id: string;
  email: string;
  display_name: string | null;
  avatar_url: string | null;
  created_at: string;
  updated_at: string;
  avatar_bg_color: string;
}

export const useProfiles = () => {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [currentProfile, setCurrentProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchProfiles = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .order('display_name', { ascending: true });

      if (error) throw error;
      setProfiles(data || []);
    } catch (error) {
      console.error('Error fetching profiles:', error);
      toast({
        title: 'Fout bij laden profielen',
        description: 'Er ging iets mis bij het ophalen van de profielen.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error) throw error;
      setCurrentProfile(data);
    } catch (error) {
      console.error('Error fetching current profile:', error);
    }
  };

  const updateProfile = async (id: string, updates: Partial<Profile>) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      setProfiles(prev => prev.map(profile =>
        profile.id === id ? { ...profile, ...updates } : profile
      ));

      if (currentProfile?.id === id) {
        setCurrentProfile(prev => prev ? { ...prev, ...updates } : null);
      }

      toast({
        title: 'Profiel bijgewerkt',
        description: 'Het profiel is succesvol bijgewerkt.',
      });
    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: 'Fout bij bijwerken profiel',
        description: 'Er ging iets mis bij het bijwerken van het profiel.',
        variant: 'destructive'
      });
    }
  };

  const uploadAvatar = async (file: File) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Geen gebruiker ingelogd');

      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}-${Math.random()}.${fileExt}`;
      const filePath = `avatars/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('pictures')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('pictures')
        .getPublicUrl(filePath);

      await updateProfile(user.id, { avatar_url: publicUrl });

      return publicUrl;
    } catch (error) {
      console.error('Error uploading avatar:', error);
      toast({
        title: 'Fout bij uploaden avatar',
        description: 'Er ging iets mis bij het uploaden van de avatar.',
        variant: 'destructive'
      });
      throw error;
    }
  };

  useEffect(() => {
    fetchProfiles();
    fetchCurrentProfile();
  }, []);

  return {
    profiles,
    currentProfile,
    loading,
    refetch: fetchProfiles,
    updateProfile,
    uploadAvatar
  };
};